/**
 * Capability-based Route Guard
 *
 * Replaces the role-name-based AdminRoute with a capability-based guard.
 * Supports capability requirements, fallback redirects, and loading states.
 */
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/app/providers/AuthProvider";
import { useCapabilities } from "@/hooks/useCapabilities";
import { CAPABILITIES } from "@carmel/shared";
import type { Capability } from "@carmel/shared";

interface RequireCapabilityProps {
  /** Capabilities required to access this route. User must have ALL of these. */
  capabilities?: Capability[];
  /** Capabilities where having ANY of these grants access. */
  anyCapability?: Capability[];
  /** Where to redirect if the user doesn't have the required capabilities. */
  redirectTo?: string;
  /** Custom content to render when access is denied (instead of redirecting). */
  accessDeniedComponent?: React.ReactNode;
}

/**
 * Route guard that requires specific capabilities.
 *
 * @example
 * // Require a specific capability
 * <RequireCapability capabilities={[CAPABILITIES.DASHBOARD_VIEW_ORGANISATION]}>
 *   <OversightPage />
 * </RequireCapability>
 *
 * @example
 * // Require any of multiple capabilities
 * <RequireCapability anyCapability={[CAPABILITIES.TASK_ASSIGN_ALL, CAPABILITIES.TASK_ASSIGN_DEPARTMENT]}>
 *   <AssignTaskPage />
 * </RequireCapability>
 */
export function RequireCapability({
  capabilities: requiredCaps = [],
  anyCapability: anyCaps = [],
  redirectTo = "/home",
  accessDeniedComponent,
}: RequireCapabilityProps) {
  const { user, loading } = useAuth();
  const { hasCapability, hasAnyCapability, hasAllCapabilities } = useCapabilities();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-[var(--background)]">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-[var(--color-primary)] border-t-transparent" />
          <p className="text-sm text-[var(--color-mute)]">Loading permissions...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Check "all required" capabilities
  const hasAllRequired = requiredCaps.length === 0 || hasAllCapabilities(requiredCaps);

  // Check "any of" capabilities
  const hasAnyRequired = anyCaps.length === 0 || hasAnyCapability(anyCaps);

  if (hasAllRequired && hasAnyRequired) {
    return <Outlet />;
  }

  // Access denied
  if (accessDeniedComponent) {
    return <>{accessDeniedComponent}</>;
  }

  return <Navigate to={redirectTo} replace />;
}

/**
 * Legacy AdminRoute replacement — uses capability check instead of role name.
 */
export function AdminRoute() {
  return (
    <RequireCapability
      capabilities={[CAPABILITIES.ORGANISATION_MANAGE]}
      redirectTo="/home"
      accessDeniedComponent={<AccessDeniedPage />}
    />
  );
}

function AccessDeniedPage() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-[var(--background)] p-6">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-[var(--foreground)]">Access Denied</h1>
        <p className="mt-2 text-sm text-[var(--color-mute)]">
          You don't have permission to access this page.
        </p>
        <a
          href="/home"
          className="mt-4 inline-block rounded-[var(--radius-md)] bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
        >
          Go to Home
        </a>
      </div>
    </div>
  );
}
